import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { client } from "@duress/shared";
import { createNote, deleteNote, listNotes, updateNote } from "./notes-api";

function notesKey(clientId: string) {
  return ["client-notes", clientId] as const;
}

export function useClientNotes(clientId: string): {
  data: client.ClientNoteListResponse | undefined;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: notesKey(clientId),
    queryFn: () => listNotes(clientId),
    enabled: Boolean(clientId),
  });
  return { data, isLoading, error };
}

export function useCreateNote(clientId: string): {
  mutateAsync: (input: client.CreateClientNoteRequest) => Promise<client.ClientNote>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: client.CreateClientNoteRequest) =>
      createNote(clientId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(clientId) });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

interface UpdateArgs {
  noteId: string;
  input: client.UpdateClientNoteRequest;
}

export function useUpdateNote(clientId: string): {
  mutateAsync: (args: UpdateArgs) => Promise<client.ClientNote>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (args: UpdateArgs) =>
      updateNote(clientId, args.noteId, args.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(clientId) });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}

export function useDeleteNote(clientId: string): {
  mutateAsync: (noteId: string) => Promise<void>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (noteId: string) => deleteNote(clientId, noteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notesKey(clientId) });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}
